/**
 * Cookie Token Utility
 * 
 * Provides temporary cookie-based transport for contextToken across subdomains.
 * This is NOT a session cookie - it's a one-time handoff mechanism.
 * 
 * Flow:
 * 1. LoginPage/ContextSelector stores token in cookie after select-context
 * 2. Redirect to target subdomain
 * 3. AuthInitializer reads cookie, migrates to sessionStorage, deletes cookie
 * 
 * Security:
 * - Short-lived (5 min expiry)
 * - SameSite=Lax (CSRF protection)
 * - Secure flag (HTTPS only in production)
 * - Deleted immediately after migration
 */

import { getParentCookieDomain } from '@/utils/cookie-domain.util'

const CONTEXT_COOKIE_NAME = 'vd_ctx_tmp'
const ACCESS_COOKIE_NAME = 'vd_access_tmp'

function writeHandoffCookie(name: string, value: string): void {
  if (!value) return

  const domain = getParentCookieDomain()
  const maxAge = 300 // 5 minutes — one-time cross-subdomain handoff

  const cookieString = [
    `${name}=${encodeURIComponent(value)}`,
    ...(domain ? [`Domain=${domain}`] : []),
    'Path=/',
    'Secure',
    'SameSite=Lax',
    `Max-Age=${maxAge}`,
  ].join('; ')

  document.cookie = cookieString
}

function readHandoffCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  if (!match?.[2]) return null
  try {
    return decodeURIComponent(match[2])
  } catch {
    return match[2]
  }
}

function clearHandoffCookie(name: string): void {
  const domain = getParentCookieDomain()
  const cookieString = [
    `${name}=`,
    ...(domain ? [`Domain=${domain}`] : []),
    'Path=/',
    'Secure',
    'SameSite=Lax',
    'Max-Age=0',
  ].join('; ')

  document.cookie = cookieString
}

/**
 * Store contextToken in temporary cross-subdomain cookie
 * This cookie is used ONLY for transport, not as a session store
 * 
 * @param token - The contextToken to transport
 */
export function setContextTokenCookie(token: string): void {
  writeHandoffCookie(CONTEXT_COOKIE_NAME, token)
}

/**
 * Store accessToken for cross-subdomain navigation (login → platform/org subdomain).
 * sessionStorage and in-memory tokens do not survive window.location.replace across subdomains.
 */
export function setAccessTokenHandoffCookie(token: string): void {
  writeHandoffCookie(ACCESS_COOKIE_NAME, token)
}

/** Set both handoff cookies before a hard redirect to another subdomain. */
export function setAuthHandoffCookies(options: {
  contextToken?: string | null
  accessToken?: string | null
}): void {
  if (options.contextToken) setContextTokenCookie(options.contextToken)
  if (options.accessToken) setAccessTokenHandoffCookie(options.accessToken)
}

/**
 * Read contextToken from temporary cookie
 * Returns null if cookie doesn't exist
 * 
 * @returns The contextToken or null
 */
export function getContextTokenCookie(): string | null {
  return readHandoffCookie(CONTEXT_COOKIE_NAME)
}

export function getAccessTokenHandoffCookie(): string | null {
  return readHandoffCookie(ACCESS_COOKIE_NAME)
}

/**
 * Delete the temporary contextToken cookie
 * Called immediately after migrating to sessionStorage
 */
export function clearContextTokenCookie(): void {
  clearHandoffCookie(CONTEXT_COOKIE_NAME)
}

export function clearAccessTokenHandoffCookie(): void {
  clearHandoffCookie(ACCESS_COOKIE_NAME)
}

/**
 * Check if contextToken cookie exists
 * Useful for debugging
 */
export function hasContextTokenCookie(): boolean {
  return getContextTokenCookie() !== null
}

